import {
  html,
  attr,
  prop,
  Signal,
  When,
  Ensure,
  ForEach,
  NotEmpty,
} from '@tempots/dom'
import { Anchor } from '@tempots/ui'
import { Icon, EditableText } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { StatCard } from '@/components/ui'
import {
  formatDate,
  formatBytes,
  formatCredits,
  formatBillingMethod,
} from '@/utils/formatting'
import { organizations as orgsService } from '@/services/gateway'
import type { Organization, OrgStorageAggregation } from '@/services/types'
import { Workspace } from '@/state/workspace'

// ============================================================================
// Organization Detail Page Component
// ============================================================================

export interface OrganizationDetailPageOptions {
  id: Signal<string>
}

export function OrganizationDetailPage({ id }: OrganizationDetailPageOptions) {
  id.on(Workspace.changeOrganization)
  const org = Workspace.organization
  const orgStorage = org.mapAsync(
    async (org) => (org != null ? orgsService.getOrgStorage(org.id) : null),
    null
  )

  // Editable name state
  const updateNameError = prop<string | null>(null)

  const isOwnerOrAdmin = prop(false)

  async function handleUpdateName(name: string) {
    const trimmedName = name.trim()
    if (!trimmedName) {
      updateNameError.set('Organization name is required')
      return
    }

    try {
      const orgId = typeof id === 'string' ? id : id.get()
      const updatedOrg = await orgsService.updateOrganization(
        orgId,
        trimmedName
      )

      // Update workspace state so selector shows new name
      Workspace.setOrganizationName(orgId, updatedOrg.name)
    } catch (err) {
      updateNameError.set(
        err instanceof Error ? err.message : 'Failed to update organization'
      )
    }
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        { href: '/organizations' },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      'Organization Details'
    ),
    content: html.div(
      attr.class('space-y-6'),
      // Header with org info
      html.div(
        attr.class('flex items-stretch gap-4'),
        html.div(
          attr.class('bg-white rounded-xl border border-gray-200 p-6 flex-2'),
          html.div(
            attr.class('flex items-start gap-4'),
            html.div(
              attr.class(
                org.map((o) =>
                  o.isPersonal
                    ? 'w-16 h-16 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center'
                    : 'w-16 h-16 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center'
                )
              ),
              Icon(
                {
                  icon: org.map((o): string =>
                    o.isPersonal ? 'mdi:account' : 'mdi:domain'
                  ),
                },
                attr.class('w-8 h-8')
              )
            ),
            html.div(
              attr.class('flex-1'),
              html.div(
                attr.class('flex items-center gap-2'),
                html.div(
                  attr.class('text-2xl font-bold text-gray-900'),
                  EditableText({
                    value: org.map((o) => o.name),
                    onChange: handleUpdateName,
                    placeholder: 'Organization name',
                    disabled: isOwnerOrAdmin.map((isOwner) => !isOwner),
                  })
                ),
                When(
                  org.map((o) => o.isPersonal),
                  () =>
                    html.span(
                      attr.class(
                        'inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800'
                      ),
                      'Personal'
                    )
                )
              ),
              html.p(
                attr.class('text-sm text-gray-500 mt-1'),
                org.map((o) =>
                  o.isPersonal ? 'Your personal workspace' : 'Organization'
                )
              ),
              Ensure(updateNameError, () =>
                html.div(
                  attr.class(
                    'mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2'
                  ),
                  updateNameError
                )
              )
            )
          )
        ),
        html.div(
          attr.class('flex-1'),
          StatCard({
            title: 'Credit Balance',
            value: org.map((o) =>
              formatCredits(o.creditBalance, { includeSuffix: false })
            ),
            valueSuffix: 'cr',
            icon: 'mdi:currency-usd',
            color: 'green',
          })
        )
      ),
      // Overview Content
      OverviewTab(org, orgStorage)
    ),
  })
}

// ============================================================================
// Components
// ============================================================================

function OverviewTab(
  org: Signal<Organization>,
  orgStorage: Signal<OrgStorageAggregation | null>
) {
  return html.div(
    attr.class('space-y-6'),
    // Organization Storage Aggregation
    Ensure(orgStorage, (storage) => StorageAggregationCard(storage)),
    // Organization Info
    html.div(
      attr.class('bg-white rounded-xl border border-gray-200 p-6'),
      html.h2(
        attr.class('text-lg font-semibold text-gray-900 mb-4'),
        'Organization Information'
      ),
      html.dl(
        attr.class('grid grid-cols-1 sm:grid-cols-2 gap-4'),
        InfoRow(
          'Organization ID',
          org.map((o): string => o.id)
        ),
        InfoRow(
          'Billing Method',
          org.map((o): string => formatBillingMethod(o.billingMethod))
        ),
        InfoRow(
          'Created',
          org.map((o): string => formatDate(o.createdAt))
        ),
        InfoRow(
          'Type',
          org.map((o): string => (o.isPersonal ? 'Personal' : 'Team'))
        )
      )
    )
  )
}

// ============================================================================
// Storage Aggregation Component
// ============================================================================

function StorageAggregationCard(storage: Signal<OrgStorageAggregation>) {
  return html.div(
    attr.class('bg-white rounded-xl border border-gray-200 p-6'),
    // Header
    html.h2(
      attr.class('text-lg font-semibold text-gray-900 mb-4'),
      'Organization Storage'
    ),
    // Summary Stats
    html.div(
      attr.class('grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'),
      html.div(
        html.div(
          attr.class('text-sm font-medium text-gray-500 mb-1'),
          'Total Storage Used'
        ),
        html.div(
          attr.class('text-2xl font-bold text-gray-900'),
          storage.map((s) => formatBytes(s.total_storage_bytes))
        )
      ),
      html.div(
        html.div(
          attr.class('text-sm font-medium text-gray-500 mb-1'),
          'Total File Count'
        ),
        html.div(
          attr.class('text-2xl font-bold text-gray-900'),
          storage.map((s) => s.total_file_count.toLocaleString())
        )
      ),
      html.div(
        html.div(
          attr.class('text-sm font-medium text-gray-500 mb-1'),
          'Projects'
        ),
        html.div(
          attr.class('text-2xl font-bold text-gray-900'),
          storage.map((s) => s.project_breakdown.length.toLocaleString())
        )
      )
    ),
    // Per-project breakdown table
    html.div(
      html.h3(
        attr.class('text-sm font-semibold text-gray-700 mb-3'),
        'Per-Project Breakdown'
      ),
      When(
        storage.map((s) => s.project_breakdown.length === 0),
        () =>
          html.div(
            attr.class('text-center py-8 text-gray-500'),
            'No project storage data available'
          )
      ),
      NotEmpty(storage.$.project_breakdown, (projects) =>
        html.div(
          attr.class('overflow-x-auto'),
          html.table(
            attr.class('min-w-full divide-y divide-gray-200'),
            html.thead(
              attr.class('bg-gray-50'),
              html.tr(
                html.th(
                  attr.class(
                    'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                  ),
                  'Project Name'
                ),
                html.th(
                  attr.class(
                    'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center'
                  ),
                  'Storage Used'
                ),
                html.th(
                  attr.class(
                    'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center'
                  ),
                  'File Count'
                ),
                html.th(
                  attr.class(
                    'px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center'
                  ),
                  'Daily Cost'
                )
              )
            ),
            html.tbody(
              attr.class('bg-white divide-y divide-gray-200'),
              ForEach(projects, (project) => {
                const pid = project.map((p) => p.project_id)
                const name = pid
                  .map(Workspace.getProjectById)
                  .map((p) => p?.name ?? '')
                return html.tr(
                  attr.class('hover:bg-gray-50'),
                  html.td(
                    attr.class(
                      'px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900'
                    ),
                    Anchor(
                      {
                        href: pid.map((p) => `/p/${p}`),
                      },
                      attr.class(
                        'text-brand-600 hover:text-brand-700 hover:underline'
                      ),
                      name
                    )
                  ),
                  html.td(
                    attr.class(
                      'px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-right'
                    ),
                    project.map((p) => formatBytes(p.storage_used_bytes))
                  ),
                  html.td(
                    attr.class(
                      'px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-right'
                    ),
                    project.map((p) => p.file_count.toLocaleString())
                  ),
                  html.td(
                    attr.class(
                      'px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-right'
                    ),
                    project.map((p) => `${p.daily_cost.toFixed(2)} cr/day`)
                  )
                )
              })
            )
          )
        )
      )
    )
  )
}

// ============================================================================
// Utility Components
// ============================================================================

function InfoRow(label: string, value: Signal<string>) {
  return html.div(
    html.dt(attr.class('text-sm font-medium text-gray-500'), label),
    html.dd(attr.class('mt-1 text-sm text-gray-900'), value)
  )
}
